// MACHINERY V2 (Stroje) — fleet card kebab menu (M2.2).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.2 + §10 (permissions).
// Reference impl: design_handoff_machinery_v3/machinery-live.jsx (QuickMenu,
// QUICK_MENU_ITEMS) — rebuilt on Radix DropdownMenu + the `.ms-menu` atom
// instead of the handoff's hand-rolled absolute-positioned popup.
//
// ACTION SET = THE DATA PACKAGE, NOT THE ARTBOARD. The artboard draws an
// abbreviated menu (Profil / QR / Vytvoriť SS / Vyradiť); the data package §9
// "Actions" row lists the full live set, and the golden rule is that the data
// package wins where the artboard omits. So Create RFS / Edit / Assign group /
// Duplicate / Restore are all here, each behind the same gate the live grid
// applies.
//
// PERMISSIONS (§10) — one gate per row, every one of them living in
// fleetActions.ts (this component only paints the list it is handed):
//   Read  : Profil, Vytvoriť servisnú správu  (Workers included)
//   Write : QR kód, Vytvoriť servisnú žiadosť, Upraviť detaily, Duplikovať,
//           Priradiť do skupiny, Vyradiť, Obnoviť
//   Admin : Zmazať natrvalo — hard delete, decommissioned machines only
//           (SHARED_DEPLOY_AND_RULES §6 "Hard delete endpoints = Admin").
//   THIS IS NOT BLANKET LIVE PARITY. Two rows differ in kind:
//   · QR IS Machinery:WRITE (SEC-REQ-B7-12) on EVERY branch, decommissioned
//     included — its dialog reads a Write-level nameplate GET, so a Worker
//     would get a 403 and an empty dialog. Live AGREES: all four live surfaces
//     wrap it in PermissionGuard(Machinery, Write), so this row is straight
//     conformance, not a new gate. Do not ungate; the four guard lines are
//     cited in fleetActions.ts.
//   · OBNOVIŤ is Write in v2 but UNGATED on all four live surfaces. That gap
//     is a DELIBERATE, approved divergence and v2 is the correct side: the
//     restore endpoint is Write-only server-side
//     (CustomerMachinesController.cs:364-365), so live's ungated button 403s
//     for every Worker who clicks it. Live's is the logged defect — do NOT
//     ungate to "match live". Full citation in fleetActions.ts.
//   Profil is the one Read row that survives on a decommissioned machine —
//   unconditional there, matching the live cards.
//
// THIS COMPONENT HAS TWO CONSUMERS AND THEY COLLAPSE DIFFERENTLY. Do not
// describe one and assume the other:
//   · FLEET (FleetCard / FleetRow) — a decommissioned machine gives
//     Profil · QR · Obnoviť · Zmazať natrvalo to an Admin, the same minus the
//     delete row to a Write user, and Profil alone to a Worker. The list is
//     never empty, so the kebab always renders.
//   · PROFILE (profile/ProfileHero via useProfileActions.ts) — the same state
//     collapses HARDER, because PROMOTED_OUT_OF_KEBAB filters `profile`
//     (self-referential on the profile), `restore` AND `delete` (both promoted
//     into ProfileDecommissionBanner), while `reportProblem` is suppressed on a
//     decommissioned machine. An Admin and a Write user are therefore BOTH left
//     with QR ONLY, and a Worker with an EMPTY list — which this component renders as no kebab
//     at all (the `actions.length === 0` early return below). That empty case
//     is the profile's alone; ProfileHero additionally guards its whole action
//     column so nothing renders a blank 190px gutter.
//
// PORTAL RULE (MachineryV2Layout.tsx:20-39): DropdownMenu.Content portals to
// document.body, outside `.machinery-v2-root`, where the scoped vars do not
// resolve. Content re-carries the scope class, sets inline
// background:"transparent", paints on the inner `.ms-menu` div, and reads
// `palette` live on EVERY render. Same countermeasure as
// chrome/ChromeRightMenus.tsx `MenuContent` (canonical).
//
// STOP PROPAGATION — MOUSE **AND** KEYBOARD, AND IT LIVES HERE (M2.6, C4).
// This menu sits inside surfaces whose own onClick/onKeyDown navigate to the
// machine or the report. Radix portals the DOM to <body>, but React synthetic
// events still bubble the COMPONENT tree into the opener, so:
//   · MOUSE    — the trigger stops click propagation (:131 below) and the
//                portalled content does the same (:160), or picking
//                "Duplikovať" would also navigate away mid-action.
//   · KEYBOARD — `DropdownMenu.Trigger` calls preventDefault() on Enter/Space
//                but does NOT stop propagation, so a keyboard user who tabbed
//                to the kebab and pressed Enter opened the menu AND fired the
//                parent's onKeyDown underneath it. The trigger now stops those
//                two keys as well (:132-134).
// STANDING RULE: popup / tooltip / menu content inside a clickable surface must
// stopPropagation on the MOUSE **AND** the KEYBOARD activation paths. One
// without the other is not a guard.
// The guard is CENTRAL here rather than per call site, so fleet cards, fleet
// rows, the profile hero and the SR list all inherit it. The two call-site
// wrappers M2.5 added (profile/tabs/srRows.tsx:124-131 and :185-193 — the
// precedent this generalises, personally re-read at M2.6) are LEFT IN PLACE:
// belt-and-braces on a destructive-navigation path, and removing shipped guards
// buys nothing.
// Only the two ACTIVATION keys are stopped — Radix's own keyboard navigation
// inside an OPEN menu (arrows, Escape, typeahead) is untouched, because those
// keys are handled on the portalled Content, not on this trigger.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";

const KebabGlyph: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
    <circle cx="7" cy="2.5" r="1.2" />
    <circle cx="7" cy="7" r="1.2" />
    <circle cx="7" cy="11.5" r="1.2" />
  </svg>
);

export interface FleetMenuAction {
  id: string;
  labelKey: string;
  onSelect: () => void;
  /** Renders in --red and sits below a separator. */
  danger?: boolean;
  /** Radix suppresses onSelect entirely; the row also dims. Used for an action
   *  whose flow is already running (MDEL-FE review finding 2) — the menu closes
   *  on select, so this is what a user sees if they re-open it mid-flight. */
  disabled?: boolean;
}

export interface FleetCardMenuProps {
  actions: FleetMenuAction[];
  ariaLabel: string;
}

const FleetCardMenu: React.FC<FleetCardMenuProps> = ({ actions, ariaLabel }) => {
  const { translate } = useTranslation("myMachinery");
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);

  if (actions.length === 0) return null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          // The card navigates on click; opening its own menu must not.
          onClick={(e) => e.stopPropagation()}
          // ...and neither must opening it from the keyboard. See the header.
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") e.stopPropagation();
          }}
          style={{
            width: 28,
            height: 28,
            padding: 0,
            border: "1px solid var(--border)",
            background: "var(--card)",
            color: "var(--ink-2)",
            cursor: "pointer",
            borderRadius: 6,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <KebabGlyph />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={`machinery-v2-root p-${palette} mod-stroje`}
          align="end"
          sideOffset={6}
          // Same reason as the trigger: a click that lands on the menu surface
          // (a gap, a separator) must not fall through to the card.
          onClick={(e) => e.stopPropagation()}
          style={{ background: "transparent", zIndex: 1290 }}
        >
          {/* role="none": Radix puts role="menu" on Content and role="menuitem"
              on each Item; this presentational box sits between them and must
              not break the ARIA parent-child relationship. */}
          <div className="ms-menu" role="none" style={{ width: 214 }}>
            {actions.map((a, i) => (
              <React.Fragment key={a.id}>
                {a.danger && i > 0 && (
                  <div
                    style={{
                      height: 1,
                      background: "var(--border-soft)",
                      margin: "5px 4px",
                    }}
                  />
                )}
                <DropdownMenu.Item
                  className="mi"
                  onSelect={a.onSelect}
                  disabled={a.disabled}
                  style={{
                    ...(a.danger ? { color: "var(--red)" } : null),
                    ...(a.disabled ? { opacity: 0.45, cursor: "default" } : null),
                  }}
                >
                  <span>{translate(a.labelKey)}</span>
                </DropdownMenu.Item>
              </React.Fragment>
            ))}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default FleetCardMenu;
